import React from 'react';
import CircularProgress from '../../../common/CircularProgress';

interface ValidationResultPanelProps {
  status: 'validating' | 'approved' | 'needs_additional_document' | 'rejected_escalated';
  userFeedback?: string | null;
  /** Solo relevante para needs_additional_document. */
  additionalDocumentInputId?: string;
  additionalDocumentFileName?: string | null;
  onAdditionalDocumentSelected?: (file: File) => void;
}

/**
 * Resultado de la validación IA de un certificado de servicio. 3 variantes según status
 * (+ un estado transitorio "validating" mientras corre la llamada). Los footers de
 * needs_additional_document y rejected_escalated son texto FIJO — nunca vienen del modelo
 * (ver validacion-ia-certificados-servicios.md, sección "Feedback al usuario").
 */
export const ValidationResultPanel: React.FC<ValidationResultPanelProps> = ({
  status,
  userFeedback,
  additionalDocumentInputId = 'cert-additional-document',
  additionalDocumentFileName,
  onAdditionalDocumentSelected,
}) => {
  if (status === 'validating') {
    return (
      <div className="flex items-center gap-3 rounded-xl bg-purple-50 border border-purple-100 px-4 py-3">
        <CircularProgress sizeRem={1.25} color="#6D57BC" />
        <span className="text-sm text-purple-800 font-medium">Validando certificado...</span>
      </div>
    );
  }

  if (status === 'approved') {
    return (
      <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3">
        <p className="text-sm text-green-800 font-medium">
          ✅ Certificado validado. El servicio ya está activo en tu perfil.
        </p>
      </div>
    );
  }

  if (status === 'needs_additional_document') {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex flex-col gap-3">
        <p className="text-sm text-amber-800">{userFeedback}</p>
        <div className="flex flex-col gap-1">
          <label htmlFor={additionalDocumentInputId} className="text-xs font-semibold text-amber-800">
            Subí el plan de estudios, programa o cronograma del curso
          </label>
          <input
            id={additionalDocumentInputId}
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              if (f) onAdditionalDocumentSelected?.(f);
            }}
          />
          {additionalDocumentFileName && (
            <span className="text-xs text-green-700 font-medium">&#10003; {additionalDocumentFileName}</span>
          )}
        </div>
        <p className="text-xs text-amber-700 italic leading-relaxed">
          Esta validación es automática. El equipo de Psi Mammoliti puede revisar o corregir el resultado en
          cualquier momento.
        </p>
      </div>
    );
  }

  // rejected_escalated
  return (
    <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex flex-col gap-2">
      <p className="text-sm text-red-800">{userFeedback}</p>
      <p className="text-xs text-red-700 italic leading-relaxed">
        Esta validación es automática. Si considerás que esto es un error o querés que evaluemos tu caso en
        particular, escribinos a{' '}
        <a href="mailto:administracion@psimammoliti.com" className="font-semibold underline">
          administracion@psimammoliti.com
        </a>
        .
      </p>
    </div>
  );
};

export default ValidationResultPanel;
